import type { CSSProperties } from 'react'

import { renderAs, type PolymorphicProps } from '@/components/motion/polymorphic'
import { trackMarkerStyle } from '@/lib/motion/track-marker'
import { cn } from '@/lib/utils'

interface MarkerOwnProps {
  /** Where it sits along its track, 0 to 100. It slides in from the track's start to here. */
  at: number
  /** Its look and offsets. A `left` in `style` overrides the spot, not the slide's length. */
  className?: string | undefined
  style?: CSSProperties | undefined
}

/**
 * A mark on a track — a dot, a tick, a caption riding over its value — that
 * slides in from the track's start to its spot on mount (`animate-track-marker`,
 * globals.css). The track, or the row laid over it, must be `@container`: the
 * slide is measured in `cqw`.
 *
 * Inside a table it slides once, with the table: past the body's
 * `data-entered` a re-sorted or re-polled row shows it where it stands (TABLE
 * MARKS, globals.css).
 */
export default function Marker<T extends 'span' | 'div' = 'span'>({
  as,
  at,
  className,
  style,
  ...rest
}: PolymorphicProps<T, MarkerOwnProps>) {
  return renderAs(as ?? 'span', {
    ...rest,
    className: cn('animate-track-marker', className),
    style: { ...trackMarkerStyle(at), ...style },
  })
}
